import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { FunnelStage } from './PipelineFunnelChart';
import { buildFunnelThicknesses, type PipelineFlowStage } from '../../utils/campaignMetrics';

export type PipelineChartVariant = 'bars' | 'funnel';

/** A rendered stage row handed to onStageClick. PipelineFlowStage pins `key`
 *  to StatusType (FunnelStage's `key` is a loose string) — the drill-down
 *  needs the canonical status key for filtering/grouping. */
export type PipelineChartStage = PipelineFlowStage;

interface PipelineChartProps {
  stages: FunnelStage[];
  variant: PipelineChartVariant;
  /** When set, each stage becomes an interactive control (click + Enter/Space)
   *  that hands the stage to the caller — Team View opens its leads modal. */
  onStageClick?: (stage: PipelineChartStage) => void;
}

// Shared layout: value-label headroom above + stage-label row below, so BOTH
// variants get the SAME track height — the largest value always spans 100% of
// it, and toggling variants never changes the chart's footprint.
const TOP_HEADROOM = 22;
const LABEL_ROW = 20;
const MIN_TRACK = 56;
// Floor per variant: bars render values ABOVE the bar (tiny bars stay
// distinct); funnel bands hold the number INSIDE (readability floor).
const MIN_THICKNESS: Record<PipelineChartVariant, number> = { bars: 3, funnel: 26 };

/**
 * THE pipeline graph for the Team overview — one component, one data path,
 * two renders (`bars` | `funnel`). Single source of truth for measurement
 * (ResizeObserver), the shared representation scale (buildFunnelThicknesses:
 * sqrt perceptual curve, value-ordered — a bigger value can never render
 * smaller), entrance animation, and labeling. Adding a new graph type means
 * adding a branch HERE, not cloning the pipeline.
 */
export const PipelineChart = ({ stages, variant, onStageClick }: PipelineChartProps) => {
  const gradientBase = useId().replace(/[^a-zA-Z0-9-]/g, '');

  // Real container size (1:1 SVG — no viewBox scaling distortion); the
  // container fills the figure's remaining height, so the chart adapts to the
  // space the panel already gives it.
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { width, height } = size;
  // ONE thickness computation per stages+variant+size change. Zeros drop out
  // AFTER indexing — thickness[i] is always the thickness of stages[i]
  // (filtering first misaligned indices and rendered wrong bands whenever a
  // stage had 0 leads).
  const rows = useMemo(() => {
    const trackHeight = Math.max(MIN_TRACK, height - TOP_HEADROOM - LABEL_ROW);
    const thicknesses = buildFunnelThicknesses(stages, trackHeight, MIN_THICKNESS[variant]);
    return stages
      .map((stage, i) => ({ ...stage, thickness: thicknesses[i] ?? 0 }) as PipelineChartStage & { thickness: number })
      .filter((row) => row.value > 0 && row.thickness > 0);
  }, [stages, variant, height]);

  const trackHeight = Math.max(MIN_TRACK, height - TOP_HEADROOM - LABEL_ROW);
  const svgHeight = trackHeight + TOP_HEADROOM + LABEL_ROW;
  const baseline = TOP_HEADROOM + trackHeight; // bars sit on this line
  const labelFont = width / Math.max(rows.length, 1) < 70 ? 8.5 : 10;

  return (
    <div
      ref={containerRef}
      className={variant === 'funnel' ? 'team-pipeline--funnel' : 'team-pipeline-chart'}
      role="img"
      aria-label={`Lead pipeline: ${rows.map((row) => `${row.label} ${row.value}`).join(', ')}`}
    >
      {width > 0 && rows.length > 0 && (
        <svg
          width={width}
          height={svgHeight}
          viewBox={`0 0 ${width} ${svgHeight}`}
          style={variant === 'bars' ? undefined : { filter: 'drop-shadow(0 6px 20px rgba(0,0,0,0.35))' }}
        >
          <defs>
            {/* FUNNEL keeps the glossy gradient; BARS are FLAT (Manuel) — solid
                color at a calm opacity, no pulsing, no glow. One gradient per
                row is only emitted when the funnel variant needs it. */}
            {variant === 'funnel' && rows.map((row, i) => (
              <linearGradient key={row.key} id={`${gradientBase}-g-${i}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={row.color} stopOpacity={0.92}>
                  <animate attributeName="stop-opacity" values="0.92;0.74;0.92" dur="3.5s" repeatCount="indefinite" />
                </stop>
                <stop offset="100%" stopColor={row.color} stopOpacity={0.48}>
                  <animate attributeName="stop-opacity" values="0.48;0.34;0.48" dur="3.5s" repeatCount="indefinite" />
                </stop>
              </linearGradient>
            ))}
            <filter id={`${gradientBase}-shadow`} x="-10%" y="-10%" width="120%" height="120%">
              <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor="#000" floodOpacity="0.35" />
            </filter>
          </defs>

          {rows.map((row, i) => {
            const bar = variant === 'bars';
            // BARS: slimmer columns with clear air between them (Manuel) —
            // gap takes ~35% of the slot (min 12px) so bars read slim-but-
            // substantial. FUNNEL keeps its tight inter-band spacing.
            const slot = width / rows.length;
            const gap = bar ? Math.max(12, slot * 0.35) : Math.min(4, slot * 0.05);
            const midX = i * slot + slot / 2;

            // Variant geometry — the ONLY per-variant difference.
            const x = i * slot + gap / 2;
            const w = slot - gap;
            const y = bar ? baseline - row.thickness : TOP_HEADROOM + (trackHeight - row.thickness) / 2;
            // FLAT bars: tighter corner (3px) than the funnel bands.
            const r = bar ? Math.min(3, row.thickness / 2, w / 2) : Math.min(5, row.thickness / 2, w / 2);
            const pathD = [
              `M ${x + r} ${y}`,
              `L ${x + w - r} ${y}`,
              `Q ${x + w} ${y} ${x + w} ${y + r}`,
              `L ${x + w} ${y + row.thickness - r}`,
              `Q ${x + w} ${y + row.thickness} ${x + w - r} ${y + row.thickness}`,
              `L ${x + r} ${y + row.thickness}`,
              `Q ${x} ${y + row.thickness} ${x} ${y + row.thickness - r}`,
              `L ${x} ${y + r}`,
              `Q ${x} ${y} ${x + r} ${y}`,
              'Z',
            ].join(' ');

            // Value INSIDE funnel bands (centered); ABOVE bars (headroom strip).
            const valueY = bar ? y - 6 : y + row.thickness / 2;

            const interactive = Boolean(onStageClick);
            const handleStageKeyDown = interactive
              ? (event: ReactKeyboardEvent<SVGGElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onStageClick?.(row);
                  }
                }
              : undefined;

            return (
              <g
                key={row.key}
                filter={bar ? undefined : `url(#${gradientBase}-shadow)`}
                className={`transition-all duration-200 hover:brightness-115 ${interactive ? 'cursor-pointer' : ''}`}
                {...(interactive ? {
                  role: 'button' as const,
                  tabIndex: 0,
                  'aria-label': `Open ${row.label} leads (${row.value})`,
                  onClick: () => onStageClick?.(row),
                  onKeyDown: handleStageKeyDown,
                } : {})}
              >
                {/* Band/bar entrance: grows on the centerline (funnel) or from
                    the baseline (bars), staggered left → right. */}
                <path
                  d={pathD}
                  className={bar ? 'team-svg-bar' : 'team-funnel-band'}
                  style={{ animationDelay: `${i * 70}ms` }}
                  fill={bar ? row.color : `url(#${gradientBase}-g-${i})`}
                  fillOpacity={bar ? 0.78 : undefined}
                  stroke={bar ? 'none' : row.color}
                  strokeWidth={bar ? 0 : 0.5}
                  strokeOpacity={bar ? 0 : 0.25}
                />
                <text
                  x={midX}
                  y={valueY}
                  className="team-funnel-label"
                  style={{ animationDelay: `${i * 70 + 180}ms`, pointerEvents: 'none' }}
                  textAnchor="middle"
                  dominantBaseline={bar ? 'alphabetic' : 'central'}
                  fill={bar ? '#e6e9f2' : 'white'}
                  fontSize={bar ? 11 : row.thickness < 34 ? 10 : 11.5}
                  fontWeight={bar ? 600 : 900}
                  fontFamily="inherit"
                >
                  {row.value.toLocaleString()}
                </text>
                {/* Stage label in the shared bottom row. */}
                <text
                  x={midX}
                  y={svgHeight - 6}
                  className="team-funnel-label"
                  style={{ animationDelay: `${i * 70 + 180}ms`, pointerEvents: 'none' }}
                  textAnchor="middle"
                  dominantBaseline="alphabetic"
                  fill="#888"
                  fontSize={labelFont}
                  fontWeight={400}
                  fontFamily="inherit"
                >
                  {row.label}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
};
